import { Button } from "primereact/button";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import TlOnCameraSelect, { TlOnCameraValue } from "../../../components/tlOnCameraSelect/TlOnCameraSelect";
import { metricValueFormat } from "../../../utils/metricValueFormat";
import { CoachingSurveyModal } from "../associateCAP/coachingSurvey/CoachingSurveyModal";
import { useAcknowledgeActionPlanMutation, useGetCAPsessionQuery, useGetSessionByIdQuery } from "../coachingActionPlanSlice";
import { MyActionPlanView } from "../myActionPlanView/MyActionPlanView";
import "./associateAcknowledge.scss";

interface AssociateAcknowledgeProps {
  actionPlanId: number;
  sessionId: number;
  onBack?: () => void;
  via?: string;
}

const AssociateAcknowledge: React.FC<AssociateAcknowledgeProps> = ({ actionPlanId, sessionId, onBack, via }) => {
  const [data, setData] = useState<any>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [tlOnCamera, setTlOnCamera] = useState<TlOnCameraValue | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [acknowledgeActionPlan, { isLoading: isAcknowledging }] = useAcknowledgeActionPlanMutation();

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

    const { data: CAPSession } = useGetCAPsessionQuery(
        { coachingActionPlanId: actionPlanId ?? 0 },
        { skip: !actionPlanId }
    );

    useEffect(() => {
        if (CAPSession?.data && CAPSession.data.length > 0) {
          if (CAPSession.data[0].tlOnCameraResponse) {
            // Pre-populate if already acknowledged (historical view)
            setTlOnCamera(CAPSession.data[0].tlOnCameraResponse as TlOnCameraValue);
          }
        }
    }, [CAPSession]);

  const { data: sessionData } = useGetSessionByIdQuery(sessionId, {
    skip: !sessionId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (sessionData?.data) {
      const session = sessionData.data;
      setData(session);
      
    }
  }, [sessionData]);

  const formatDate = useCallback((dateStr: string): string => {
    if (!dateStr) return '';

    const date = new Date(dateStr);
    const day = date.getDate();

    const suffix = (d: number): string => {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    return date
      .toLocaleDateString('en-US', { month: 'long', year: 'numeric', day: 'numeric' })
      .replace(/\d+,/, `${day}${suffix(day)},`);
  }, []);
  
  const backLabel = useMemo(() => {
    if (via === 'historical') 
      return ' Return to Historical Action Plans';
    else
      return ' Return to My Active Action Plans';
}, [via]);

  const tlOnCameraError = useMemo(
    () => submitAttempted && !tlOnCamera,
    [submitAttempted, tlOnCamera],
  );

  const handleAcknowledge = useCallback(async () => {
    if (!actionPlanId) return;

    setSubmitAttempted(true);

    if (!tlOnCamera) return;

    try {
      const ackRequest = {
        coachingActionPlanId: actionPlanId,
        acknowledged: true,
        tlOnCameraResponse: tlOnCamera,
      };

      acknowledgeActionPlan(ackRequest)
        .unwrap()
        .then((response: any) => {
          if (response.status) {
            setAcknowledged(true);
            setShowModal(true);
          } else {
            toast.error('Acknowledge failed!');
          }
        })
        .catch((error: any) => {
          if (error) {
            toast.error('Acknowledge failed!');
          }
        });
    } catch (error) {
      console.error("Acknowledge failed!:", error);
    }
  }, [actionPlanId, tlOnCamera, acknowledgeActionPlan]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setShowSurveyModal(true);
  }, []);

  const handleSurveyHide = useCallback(() => {
    setShowSurveyModal(false);
    onBack?.();
  }, [onBack]);

  if (!data) return null;

  const isAlreadyAcknowledged = via === 'managerCAP' ? true : Boolean(data?.acknowledged);

  return (
    <div className="apc-page">
      <div className="apc-card">
        <div className="apc-top-bar no-print">
          <button className="apc-back-btn" onClick={onBack}>
            <i className="pi pi-arrow-circle-left" />{backLabel}
          </button>
          {isAlreadyAcknowledged && (
            <button
              onClick={handlePrint}
              className="apc-print-btn"
              title="Print Coaching Form"
            >
              <i className="pi pi-print" />
            </button>
          )}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '10px' }}>
          <h3 className="apc-title" style={{ margin: 0 }}>
            First Coaching Session - {formatDate(data?.session_start_date)}
          </h3>
        </div>

        <p className="apc-subtitle">
          <strong>Metric Coached:</strong> {data?.metric_label}
        </p>

        {/* Main Panel */}
        <div className="apc-panel">

          {/* Label row */}
          <div className="apc-panel__header">
            <span>Action Plan</span>
          </div>

          {/* CHT / Performance / Follow Up */}
          <div className="apc-row">
            <div className="apc-col">
              <p className="apc-section__label">{data?.metric_label}</p>
              <div className="cht-badge-wrapper">
                <span className="cht-badge">{metricValueFormat(data?.metric_current_value, data?.metric_type_name ?? '')}</span>
                <span className="cht-max"> / {metricValueFormat(data?.metric_target_value, data?.metric_type_name ?? '')}</span>
              </div>
            </div>
            <div className="apc-col apc-col--bordered">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="apc-section__label">Performance Commitment</span>
                <p className="perf-value">{metricValueFormat(data?.performance_commitment, data?.metric_type_name ?? '')}</p>
              </div>
              <p>Type: {data?.metric_type_name}</p>
            </div>
            <div className="apc-col apc-col--bordered">
              <p className="apc-section__label">Follow Up</p>
              <p className="apc-section__text">{formatDate(data?.follow_up_date)}</p>
            </div>
          </div>

          {/* Skill / Behavior / Action */}
          <div className="apc-row">
            <div className="apc-col">
              <p className="apc-section__label">Skill</p>
              <p className="apc-section__text">{data?.coaching_skill_name}</p>
            </div>
            <div className="apc-col apc-col--bordered">
              <p className="apc-section__label">Behavior</p>
              <p className="apc-section__text">{data?.coaching_behavior_name}</p>
            </div>
            <div className="apc-col apc-col--bordered">
              <p className="apc-section__label">Action</p>
              <p className="apc-section__text">{data?.coaching_action_name}</p>
            </div>
          </div>

          {/* Action Description */}
          <div className="apc-section apc-section--border-bottom">
            <p className="apc-section__label">Action Description</p>
            <p className="apc-section__text">{data?.action_description}</p>
          </div>

          {/* Success Criteria / Notes */}
          <div className="apc-row apc-row--no-border">
            <div className="apc-section apc-section--bordered-right">
              <p className="apc-section__label">Success Criteria</p>
              <p className="apc-section__text">{data?.success_criteria}</p>
            </div>
            <div className="apc-section">
              <p className="apc-section__label">Notes</p>
              <p className="apc-section__text">{data?.notes}</p>
            </div>
          </div>

        </div>

        {/* TL On Camera — shown when pending acknowledgement OR for historical view */}
        <div className="apc-camera-question">
          {/* Screen: interactive select buttons */}
          <div className="no-print">
            {via !== 'managerCAP' && (
              <div className="apc-camera-question">
                <TlOnCameraSelect
                  label="Was your Team Lead on camera for your coaching session?"
                  value={tlOnCamera}
                  onChange={setTlOnCamera}
                  required
                  error={tlOnCameraError}
                  disabled={isAlreadyAcknowledged}
                />
              </div>
            )}
          </div>
          {/* Print: static readable block, always visible on print */}
          <div className="print-only apc-camera-print">
            <span className="apc-camera-print__label">
              Was your Team Lead on camera for your coaching session?*
            </span>
            <span className="apc-camera-print__answer">
              {(() => {
                const val = tlOnCamera ?? (data?.tl_on_camera_response as TlOnCameraValue | null);
                if (!val) return 'Not answered';
                return val === 'NA' ? 'N/A' : val;
              })()}
            </span>
          </div>
        </div>

        {/* Acknowledge footer — only when not yet acknowledged */}
        {!isAlreadyAcknowledged && (
          <div className="apc-footer no-print">
            <button
              onClick={handleAcknowledge}
              disabled={isAcknowledging}
              className={`apc-ack-btn ${acknowledged ? "apc-ack-btn--done" : ""}`}
            >
              <i className="pi pi-check-circle" />
              {acknowledged ? "Acknowledged!" : "Acknowledge"}
            </button>
          </div>
        )}

        {isAlreadyAcknowledged && (
          <div>
            <MyActionPlanView CAPSession={CAPSession} />
            <div className="ra-dialog__actions justify-content-end no-print">
              <Button label="Return" className="ra-dialog__btn-secondary" onClick={onBack} />
            </div>
          </div>
        )}
      </div>

      {/* Success modal */}
      {showModal && (
        <div className="apc-modal-overlay">
          <div className="apc-modal">
            <div className="apc-modal__icon"><i className="pi pi-check" /></div>
            <h3 className="apc-modal__title">Action Plan Acknowledged</h3>
            <p className="apc-modal__text">Acknowledgement has been sent to Team Lead</p>
            <button className="apc-modal__btn" onClick={handleCloseModal}>Continue</button>
          </div>
        </div>
      )}

      <CoachingSurveyModal
        visible={showSurveyModal}
        onHide={handleSurveyHide}
        coachingActionPlanId={data?.coaching_action_plan_id}
        supervisorNumber={data?.author_employee_number}
        metricName={data?.metric_label}
        onSubmitSuccess={onBack}
      />
    </div>
  );
};

export default AssociateAcknowledge;
